import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { ProjectStatusEnum } from "@src/__generated__/graphql";
import { formatDate, Link } from "@src/components/ui-kit";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { Icon } from "@src/components/ui-kit/Icon";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import {
  TextEditor,
  type TextEditorRef,
} from "@src/components/ui-kit/TextEditor/TextEditor";
import {
  BrandLinkTitle,
  ProjectTitle,
} from "@src/components/widgets/ModuleTitle";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, { useRef } from "react";
import ArchivedBadge from "./ArchivedBadge";
import FinishedBadge from "./FinishedBadge";
import TeamMembers from "./TeamMembers";
import { UserCard } from "./UserCard";

const Header = () => {
  const { projectDetailForClientsStore: store, authStore } = useStore();
  const editorRef = useRef<TextEditorRef>(null);

  if (!store.project) {
    return null;
  }

  return (
    <Box mb="6">
      {authStore.isInternalUser && (
        <BrandLinkTitle brand={store.project.brand} />
      )}
      <Flex
        as="header"
        justify="space-between"
        direction={{
          base: "column",
          md: "row",
        }}
        w="full"
      >
        <Stack
          align="start"
          maxW={{ base: undefined, md: "60%" }}
          spacing={{
            base: "3",
            md: "4",
          }}
        >
          <VStack alignItems="start" wrap="wrap" w="full" pr="2" isTruncated>
            <ProjectTitle
              project={store.project}
              brand={store.project.brand}
              className="pr-6"
            />
            <HStack wrap="wrap" pl="12" marginInlineStart="0!" spacing="3">
              {(store.project?.status === ProjectStatusEnum.Finished &&
                !!store.project.finished_at && (
                  <FinishedBadge
                    at={new Date(store.project.finished_at)}
                    by={store.project.finishedBy?.full_name}
                  />
                )) ||
                (store.project?.status === ProjectStatusEnum.Archived &&
                  !!store.project.archived_at && (
                    <ArchivedBadge
                      at={new Date(store.project.archived_at)}
                      by={store.project.archivedBy?.full_name}
                    />
                  )) ||
                (store.project?.status === ProjectStatusEnum.Active && (
                  <React.Fragment>
                    <Badge fontWeight="medium" colorScheme="grey">
                      {formatDate(new Date(store.project.start_date))}
                      {store.project.expected_end_date
                        ? " - " +
                          formatDate(new Date(store.project.expected_end_date))
                        : null}
                    </Badge>
                  </React.Fragment>
                ))}
              {!!store.project.cloudUrl && (
                <SharepointLink href={store.project.cloudUrl} />
              )}
              {!!store.project.driveCloudUrl && (
                <GoogleDriveLink href={store.project.driveCloudUrl} />
              )}
              {!!store.project.externalLinks.length && (
                <Popover isLazy>
                  <PopoverTrigger>
                    <Button
                      colorScheme="grey"
                      rightIcon={<Icon name="chevron-down" />}
                      size="sm"
                      variant="outline"
                    >
                      <Trans>Links</Trans>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    w={{ base: "100vw", md: "600px" }}
                    maxH="400px"
                  >
                    <PopoverArrow />
                    <PopoverBody overflowY="auto">
                      {store.project.externalLinks.map((link) => (
                        <HStack
                          key={link.id}
                          py="2"
                          _notLast={{
                            borderBottom: "thin solid",
                            borderBottomColor: "gray.200",
                          }}
                        >
                          <Text flex="0.3" isTruncated>
                            {link.title}
                          </Text>
                          <Link
                            isTruncated
                            flex="0.7"
                            isExternal
                            href={link.link}
                          >
                            {link.link}
                          </Link>
                        </HStack>
                      ))}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
            </HStack>
          </VStack>
          {!!store.project?.note?.length && (
            <Box maxW="full" pl="12">
              <TextEditor
                ref={editorRef}
                isEditable={false}
                attachments={[]}
                onChange={() => {}}
                initialValue={store.project.note}
              />
            </Box>
          )}
        </Stack>

        <Stack
          direction={{
            base: "column",
            md: "row",
          }}
          spacing="4"
        >
          {/* Contacts */}
          <Flex
            as="section"
            my={{
              base: "4",
              md: "unset",
            }}
          >
            <VStack align={{ base: "start", md: "center" }} w="50%">
              <UserCard
                imageSource={store.project.projectManager.image?.urls.thumbnail}
                name={store.project.projectManager.full_name}
                teamName={store.project?.team.name}
                avatarBg={store.project.projectManager.profile?.hex_color}
              />
              <TeamMembers
                members={store.project?.otherProjectManagers ?? []}
              />
            </VStack>
            <Box w="50%">
              {store.project?.contact && (
                // TODO Add hex_color to contact
                <UserCard
                  email={store.project?.contact.email ?? ""}
                  phone={store.project?.contact.phone ?? ""}
                  imageSource={store.project?.contact.image?.urls.thumbnail}
                  name={`${store.project?.contact.first_name} ${store.project?.contact.last_name}`}
                />
              )}
            </Box>
          </Flex>
        </Stack>
      </Flex>
    </Box>
  );
};

export default observer(Header);
